import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import IpWhiteList, { IpWhiteListState, IpWhiteListTableModel } from '@models/IpWhiteList';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IpWhiteListService } from '@services/ip-white-list.service';
import { I18NextPipe } from 'angular-i18next';
import { cloneDeep, isNil } from 'lodash';
import {
  AlertComponent,
  ButtonComponent,
  ConfirmReason,
  FormsUtil,
  GroupButtonComponent,
  InputComponent,
  MbsPopupType,
  ModalService,
  ModalSettings,
  SidepanelComponent,
  SidepanelCrudBase,
  SidepanelService,
  SortEvent,
  SwitcherComponent,
  TableCellDirective,
  TableComponent,
  ToastService
} from 'mbs-ui-kit';
import { SidepanelId } from 'mbs-ui-shared@models/sidepanel';
import { IPUtils } from 'mbs-ui-shared@utils/ip';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';

import { CanPipe } from 'ability';

import { CommonModule } from '@angular/common';

import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

import { RouterLink } from '@angular/router';
import {
  createTableHeaders,
  haveCombinedIPError,
  haveIntersectedIPError,
  haveInvalidIPError,
  parseRanges
} from './sidepanel-ip-white-list.constants';

@UntilDestroy()
@Component({
  selector: 'mbs-sidepanel-ip-white-list',
  templateUrl: './sidepanel-ip-white-list.component.html',
  styleUrls: ['./sidepanel-ip-white-list.component.scss'],
  providers: [{ provide: IpWhiteListService, useClass: IpWhiteListService, deps: [HttpClient] }],
  imports: [
    AlertComponent,
    ButtonComponent,
    CanPipe,
    CommonModule,
    FormsModule,
    GroupButtonComponent,
    InputComponent,
    NgbTooltipModule,
    ReactiveFormsModule,
    RouterLink,
    SidepanelComponent,
    SwitcherComponent,
    TableComponent,
    I18NextPipe,
    TableCellDirective
  ]
})
export class SidepanelIpWhiteListComponent extends SidepanelCrudBase<any> implements OnInit, OnDestroy {
  private listingService = inject(IpWhiteListService);
  private toastService = inject(ToastService);
  private modal = inject(ModalService);
  private i18Next = inject(I18NextPipe);
  sidepanel = inject(SidepanelService);
  private fb = inject(FormBuilder);

  public readonly sidepanelId = SidepanelId.IPWhiteList;
  public headers = createTableHeaders(this.i18Next);

  public enabled = false;

  public editModel: IpWhiteListTableModel = undefined;
  // require for hid inputs when id == undefined
  public editIndex = -1; // visible editIndex

  private _data: IpWhiteList[] = [];
  public visibleData: IpWhiteListTableModel[] = [];

  set data(data: IpWhiteList[]) {
    this.setData(data);
  }

  get data(): IpWhiteList[] {
    return this._data;
  }

  public searchForm = this.fb.group({
    search: null as string
  });

  private searchFilter = this.searchForm.get('search');

  public currentIP: string;

  ipValidator = (control: AbstractControl) => {
    if (!control.value) {
      return { message: this.i18Next.transform('mbs-ui:ipWhiteList.ipRequired') };
    }

    if (haveCombinedIPError(control.value)) {
      return { ipValidator: { message: this.i18Next.transform('mbs-ui:ipWhiteList.ipCombineError') } };
    }

    if (haveInvalidIPError(control.value)) {
      return { ipValidator: { message: this.i18Next.transform('mbs-ui:ipWhiteList.invalidIP') } };
    }

    // range intersection check
    const realIndex = this.editModel.state === IpWhiteListState.Add ? -1 : this.visibleData[this.editIndex]?.index;

    if (
      haveIntersectedIPError(
        control.value,
        this.data.filter((d, index) => index !== realIndex && d.state != IpWhiteListState.Delete)
      )
    ) {
      return { ipValidator: { message: this.i18Next.transform('mbs-ui:ipWhiteList.ipAlreadyUsed') } };
    }

    return null;
  };

  nameValidator = (control: AbstractControl) => {
    if (!control.value || control.value.trim().length < 2) {
      return null;
    }

    const realIndex = this.editModel.state === IpWhiteListState.Add ? -1 : this.visibleData[this.editIndex]?.index;

    if (
      this.data.some((d, index) => index !== realIndex && d.description === control.value.trim() && d.state !== IpWhiteListState.Delete)
    ) {
      return { nameValidator: { message: this.i18Next.transform('mbs-ui:ipWhiteList.nameAlreadyUsed') } };
    }

    return null;
  };

  public editRange = new FormControl('', { validators: [this.ipValidator] });
  public editName = new FormControl('', [Validators.required, Validators.minLength(2), this.nameValidator]);
  public IPWhiteListState = IpWhiteListState;

  public searchInput = '';
  public sort: SortEvent = { direction: 'asc', column: 'name' };

  private completeEdit = new EventEmitter<IpWhiteList>(true);
  private readonly defaultDescription = 'Admin';
  public readonly alertType = MbsPopupType;

  constructor() {
    super();

    this.detectChanges$ = this.detectChanges$.pipe(
      switchMap((modalResult: ConfirmReason) => {
        if (modalResult === ConfirmReason.DISCARD) {
          this.hold();
          this.close.emit();
        }

        return of(modalResult);
      })
    );

    this.sidepanel.onOpen.pipe(debounceTime(100), untilDestroyed(this)).subscribe({
      next: (data) => {
        if (data?.id === this.sidepanelId) {
          this.initData();
        }
      }
    });
  }

  initData(): void {
    this.data = [];
    this.searchInput = '';
    this.searchFilter.reset();
    this.resetEditMode();

    this.fetchData();
  }

  ngOnInit(): void {
    this.loadingData = true;
    this.listingService.ips$.pipe(untilDestroyed(this)).subscribe((d) => {
      this.enabled = d.restrictionByIpsOn;
      this.updateData(d.whiteLists);
      this.hold();
      this.loadingData = false;
    });

    this.listingService
      .getCurrentIp()
      .pipe(untilDestroyed(this))
      .subscribe((ip) => (this.currentIP = ip));

    this.searchFilter.valueChanges
      .pipe(startWith(null), distinctUntilChanged(), debounceTime(500), untilDestroyed(this))
      .subscribe((value) => {
        this.searchInput = value;
        this.data = Array.from(this.data);
      });

    this.editRange.valueChanges.pipe(untilDestroyed(this)).subscribe((IPString) => {
      if (this.needFixIP(IPString || '')) {
        this.editRange.reset(this.fixIP(IPString || ''));
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy();
    // empty
  }

  fetchData(): void {
    this.listingService.get();
  }

  updateData(source: IpWhiteList[]): void {
    let newData: IpWhiteList[] = this.data.filter((d) => d.state > IpWhiteListState.None);
    source.forEach((d) => {
      const find = newData.find((newD) => d.id === newD.id);
      if (!find) {
        newData.push(d);
      }
    });

    if (newData.length === 0) {
      newData = source;
    }
    this.data = newData;
    this.attachObject('ipWhiteListKey', () => ({ data: cloneDeep(this.data), enabled: this.enabled }));
  }

  setData(newData: IpWhiteList[] = Array.from(this.data)): void {
    this._data = newData;

    let resultData: IpWhiteListTableModel[] = this.data.map((d, index) => {
      return { ...d, index } as IpWhiteListTableModel;
    });

    if (this.searchInput) {
      resultData = resultData.filter((d) => {
        return (
          d.description.toLocaleLowerCase().includes(this.searchInput.toLocaleLowerCase()) ||
          (d.ipRanges ? d.ipRanges.includes(this.searchInput) : false) ||
          (d.ips ? d.ips.includes(this.searchInput) : false)
        );
      });
    }

    resultData = resultData.sort((a, b) =>
      this.sort.column == 'name' ? (this.sort.direction == 'asc' ? 1 : -1) * a.description.localeCompare(b.description) : 0
    );

    this.visibleData = (
      this.editIndex > -1 && this.editModel.state === IpWhiteListState.Add ? [{ ...this.editModel, index: 0 }] : []
    ).concat(resultData);
  }

  // Add new row
  handleAddNew(): void {
    this.handleEdit(new IpWhiteListTableModel(), 0);
    this.editModel.state = IpWhiteListState.Add;
    this.data = Array.from(this.data);
  }

  handleEditReset(): void {
    const editData = this.editModel.state === IpWhiteListState.Add ? null : Object.assign({}, this.visibleData[this.editIndex]);
    this.editRange.patchValue((editData?.ips || []).concat(editData?.ipRanges || []).join('\n'));
    this.editName.patchValue(editData?.description || '');
  }

  // Remove added row
  handleEditCancel(): void {
    this.resetEditMode();
  }

  // Edit exists
  handleEdit(model: IpWhiteListTableModel, index: number): void {
    this.editModel = Object.assign({}, model);
    this.editModel.state = IpWhiteListState.Change;
    this.editRange.patchValue((this.editModel.ips || []).concat(this.editModel.ipRanges || []).join('\n'));
    this.editName.patchValue(this.editModel.description);
    this.editIndex = index;
    this.searchFilter.disable();
  }

  // Delete exists
  handleDeleteRange(model: IpWhiteListTableModel): void {
    // remove instantly
    if (isNil(model.id)) {
      this.data.splice(model.index, 1);
    } else {
      model.state = IpWhiteListState.Delete;
      this.data[model.index] = model;
    }

    this.data = Array.from(this.data);

    // disable settings for prevent lose access
    if (this.enabled && !this.currentIPInRanges()) {
      this.enabled = false;
    }
  }

  /**
   * Save exists edit row
   * @param {IpWhiteList} model copy row ip white list
   * @param {number} index index into source data
   */
  handleEditSave(model: IpWhiteListTableModel): void {
    if (this.editRange.invalid || this.editName.invalid) {
      FormsUtil.triggerValidation(this.editRange);
      FormsUtil.triggerValidation(this.editName);
      return;
    }
    if (model) {
      const updateModel = (newModel: Partial<IpWhiteList>) => {
        model.ips = newModel.ips;
        model.ipRanges = newModel.ipRanges;
        model.description = newModel.description;

        if (model.state === IpWhiteListState.Add) {
          this.data = [model as IpWhiteList].concat(this.data);
        } else {
          this.data[model.index] = model;
        }

        this.completeEdit.emit(model);
        this.resetEditMode();
      };

      const newRanges = parseRanges(this.editRange.value);

      newRanges.description = this.editName.value;

      updateModel(newRanges);
    } else {
      this.resetEditMode();
    }
  }

  isValidSidepanel(): boolean {
    return this.editIndex < 0 || (this.editRange.valid && this.editName.valid);
  }

  // Save all sidepanel data
  handleSave(): Observable<boolean> {
    if (!this.isValidSidepanel()) {
      FormsUtil.triggerValidation(this.editName);
      FormsUtil.triggerValidation(this.editRange);
      return of(false);
    }

    this.handleEditSave(this.visibleData[this.editIndex]);

    return of(this.enabled && !this.currentIPInRanges()).pipe(
      switchMap((state) => (state ? this.showConfirmModal() : of(true))),
      switchMap(() => this.listingService.update({ restrictionOn: this.enabled, whiteLists: this.data })),
      tap(() => {
        this.data = this.data.filter((d) => d.state != IpWhiteListState.Delete);
        this.data.forEach((d) => (d.state = IpWhiteListState.None));
        this.toastService.success(this.i18Next.transform('shared:notifications.successfullySaved'));
        this.save.emit();
        this.hold();
      }),
      map(() => true),
      catchError(() => of(false))
    );
  }

  handleSort(sort: SortEvent): void {
    if (this.editIndex > -1) {
      this.handleEditCancel();
    }
    this.sort = sort;
    this.data = Array.from(this.data);
  }

  handleDelete(): Observable<boolean> {
    return of(false);
  }

  handleEnableSettings(): void {
    if (this.enabled && !this.currentIPInRanges()) {
      if (this.editIndex > -1) {
        this.handleEditCancel();
      }
      const currentIpModel = new IpWhiteList();
      currentIpModel.state = IpWhiteListState.Add;
      const maxDefaultDescrCount = this.data
        .filter((d) => d.state != IpWhiteListState.Delete && d.description.startsWith(this.defaultDescription))
        .map((d) => parseInt(d.description.replace(this.defaultDescription, ''), 10))
        .filter((num) => !isNaN(num))
        .reduce((max, num) => (num > max ? num : max), 0);
      currentIpModel.description = `${this.defaultDescription}${maxDefaultDescrCount + 1}`;
      currentIpModel.ips = [this.currentIP];
      this.data = [currentIpModel].concat(this.data);
    }
  }

  private showConfirmModal(): Promise<boolean> {
    const settings: ModalSettings = {
      header: {
        title: this.i18Next.transform('mbs-ui:ipWhiteList.confirmationTitle')
      }
    };

    const msg = this.i18Next.transform('mbs-ui:ipWhiteList.confirmation');

    return this.modal.confirm(settings, msg);
  }

  private resetEditMode(): void {
    this.editModel = null;
    this.editRange.reset();
    this.editName.reset();
    this.editIndex = -1;
    this.searchFilter.enable();
    this.data = Array.from(this.data);
  }

  private currentIPInRanges(): boolean {
    return this.data
      .filter((d) => d.state != IpWhiteListState.Delete)
      .some(
        (row) =>
          (row.ips || []).some((ip) => IPUtils.containsIpInRange(this.currentIP, [ip, ip])) ||
          (row.ipRanges || []).some((range) => IPUtils.containsIpInRange(this.currentIP, range.split('-')))
      );
  }

  private needFixIP(IPString = ''): boolean {
    const spaceWrapped = IPString.startsWith(' ') || IPString.endsWith(' ');
    const commaWrapped = IPString.startsWith(',') || IPString.endsWith(',');
    const unnecessarySpace = IPString.includes(' ');
    const unnecessaryComma = IPString.includes(',,');

    return spaceWrapped || commaWrapped || unnecessarySpace || unnecessaryComma;
  }

  private fixIP(IPString = ''): string {
    return IPString.trim()
      .replace(/\s+/g, ' ')
      .replace(/ /g, ',')
      .replace(/,+/g, ',')
      .replace(/^,+|,+$/g, '');
  }
}
